import { useIsMobile } from "@/hooks/use-mobile";
import { cdnVideoSrc } from "@/lib/cdnVideo";
import type { VideoHTMLAttributes } from "react";
import { useEffect, useRef, useState } from "react";

interface LazyVideoProps extends Omit<
    VideoHTMLAttributes<HTMLVideoElement>,
    "src" | "poster"
> {
    src: string;
    poster?: string;
    rootMargin?: string;
}

const LazyVideo = ({
    src,
    poster,
    rootMargin = "200px",
    autoPlay = true,
    muted = true,
    loop = true,
    playsInline = true,
    className,
    children,
    onLoadedData,
    onError,
    ...props
}: LazyVideoProps) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [shouldLoad, setShouldLoad] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const isMobile = useIsMobile();

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        if (typeof IntersectionObserver === "undefined") {
            setShouldLoad(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setShouldLoad(true);
                    observer.disconnect();
                }
            },
            { rootMargin },
        );

        observer.observe(video);
        return () => observer.disconnect();
    }, [rootMargin]);

    useEffect(() => {
        if (!shouldLoad || !videoRef.current || !autoPlay) return;

        const attempt = videoRef.current.play();
        if (attempt && typeof attempt.catch === "function") {
            attempt.catch(() => {});
        }
    }, [autoPlay, shouldLoad]);

    const showLoadingBar = !loaded && !poster;

    return (
        <div
            className={`relative h-full w-full overflow-hidden ${className ?? ""}`}
        >
            <video
                ref={videoRef}
                className="h-full w-full object-cover"
                preload="none"
                poster={poster}
                autoPlay={autoPlay}
                muted={muted}
                loop={loop}
                playsInline={playsInline}
                onLoadedData={(e) => {
                    setLoaded(true);
                    onLoadedData?.(e);
                }}
                onError={(e) => {
                    setLoaded(true);
                    onError?.(e);
                }}
                {...props}
            >
                {shouldLoad && (
                    <>
                        {/* Device-appropriate variant first; original as a
                            fallback so a missing -d/-m degrades gracefully. */}
                        {cdnVideoSrc(src, isMobile) !== src && (
                            <source
                                src={cdnVideoSrc(src, isMobile)}
                                type="video/mp4"
                            />
                        )}
                        <source
                            src={src}
                            type={
                                src.endsWith(".webm")
                                    ? "video/webm"
                                    : "video/mp4"
                            }
                        />
                    </>
                )}
                {children}
            </video>
            {showLoadingBar && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <div className="relative w-20 h-1 bg-white/20 rounded-full overflow-hidden">
                        <div className="absolute inset-y-0 left-0 w-1/2 bg-white rounded-full animate-loading-bar" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default LazyVideo;
